import { DriverAuthenticationManager } from '@frontend/authentication-v2';
import { DeviceClient } from '@frontend/device/api';
import { DeviceType } from '@frontend/device/types';
import { ErrorHandler } from '@frontend/error-handler';
import { IoTClient } from '@frontend/iot/api';
import { IoTBrand, IoTProvision, IoTType } from '@frontend/iot/types';
import { Logger } from '@frontend/logger';
import { SettingClient } from '@frontend/setting/api';
import { SpotClient } from '@frontend/spot/api';
import { useEffect, useState } from 'react';

import { useAppDispatch } from '../../redux/store';
import { setDeviceInfo, setIotSettings, setProvisioning, setSpotInfo } from '../../redux/terminal-navigation-slice';
import getIdentifier from '../../utils/identifier-handler';
import { Props, ProvisionState } from './provision.component';

interface ViewProps {
    state: ProvisionState;
    message: string;
}

const useProvision = (props: Props): ViewProps => {
    const dispatch = useAppDispatch();
    const [authenticationManager] = useState<DriverAuthenticationManager>(DriverAuthenticationManager.getInstance('token'));
    const [state, changeState] = useState<ProvisionState>(ProvisionState.INIT);
    const [message, changeMessage] = useState<string>('Gathering info');
    const [provisionInfo, changeProvisionInfo] = useState<IoTProvision | null>(null);

    useEffect(() => {
        switch (state) {
            case ProvisionState.INIT:
                changeMessage('Gathering info');
                break;
            case ProvisionState.GATHERING_INFO_FAILED:
                changeMessage('Gathering info failed. No identifier found.');
                break;
            case ProvisionState.AUTHENTICATING:
                changeMessage('Authenticating');
                break;
            case ProvisionState.AUTHENTICATING_FAILED:
                changeMessage('Authentication failed, make sure to enable this terminal on the platform.');
                break;
            case ProvisionState.PERMISSION_CHECK:
                changeMessage('Provision check');
                break;
            case ProvisionState.DONE:
                changeMessage('Provisioning done - Redirecting');
                break;
        }
    }, [state]);

    useEffect(() => {
        switch (state) {
            case ProvisionState.INIT:
                changeState(ProvisionState.GATHERING_INFO);
                break;
            case ProvisionState.GATHERING_INFO:
                try {
                    changeProvisionInfo({
                        identifier: getIdentifier(),
                        type: IoTType.SERVER,
                        brand: IoTBrand.ANDROID
                    });
                    changeState(ProvisionState.AUTHENTICATING);
                    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
                } catch (e) {
                    changeState(ProvisionState.GATHERING_INFO_FAILED);
                }
                break;
            case ProvisionState.AUTHENTICATING:
                authenticationManager
                    .authenticate(provisionInfo!)
                    .then(() => changeState(ProvisionState.PERMISSION_CHECK))
                    .catch((e) => {
                        ErrorHandler.handleError(e);
                        setRetry(() => changeState(ProvisionState.INIT));
                        changeState(ProvisionState.AUTHENTICATING_FAILED);
                    });
                break;
            case ProvisionState.AUTHENTICATING_FAILED:
                setRetry(() => changeState(ProvisionState.AUTHENTICATING));
                break;
            case ProvisionState.PERMISSION_CHECK:
                try {
                    authenticationManager
                        .waitForToken()
                        .then((token) => {
                            IoTClient.fetchIoT(token.account_id, token.entity_id)
                                .then((iot) => {
                                    SettingClient.fetchSettings({ entity_id: iot.id })
                                        .then((settings) => {
                                            const settingsMap: { [key: string]: string } = {};
                                            settings.results.forEach((setting) => {
                                                settingsMap[setting.key] = setting.value as string;
                                            });
                                            dispatch(setIotSettings(settingsMap));
                                        })
                                        .catch((e) => {
                                            ErrorHandler.handleError(e);
                                        });
                                    if (iot.spot_id) {
                                        SpotClient.fetchSpot(iot.account_id, iot.spot_id)
                                            .then((spot) => {
                                                SettingClient.fetchSettings({ entity_id: spot.id })
                                                    .then((settings) => {
                                                        const settingsMap: { [key: string]: string } = {};
                                                        settings.results.forEach((setting) => {
                                                            settingsMap[setting.key] = setting.value as string;
                                                        });
                                                        dispatch(setSpotInfo({ name: spot.name, id: spot.id, settings: settingsMap }));
                                                    })
                                                    .catch((e) => {
                                                        ErrorHandler.handleError(e);
                                                    });
                                            })
                                            .catch((e) => {
                                                ErrorHandler.handleError(e);
                                            });
                                    }
                                    DeviceClient.fetchDevices({ iot_id: iot.id, type: DeviceType.TERMINAL })
                                        .then((devices) => {
                                            const found = devices.results.find((d) => d.type == DeviceType.TERMINAL);
                                            if (found) {
                                                dispatch(setDeviceInfo({ iot, device: found }));
                                                changeState(ProvisionState.DONE);
                                            } else {
                                                changeState(ProvisionState.PERMISSION_CHECK_FAILED);
                                                setRetry(() => changeState(ProvisionState.INIT));
                                                changeMessage('No devices found with type terminal.');
                                            }
                                        })
                                        .catch((e) => {
                                            ErrorHandler.handleError(e);
                                            changeState(ProvisionState.PERMISSION_CHECK_FAILED);
                                            setRetry(() => changeState(ProvisionState.INIT));
                                            changeMessage('Something went wrong trying to find the DEVICE.');
                                        });
                                })
                                .catch((e) => {
                                    ErrorHandler.handleError(e);
                                    changeState(ProvisionState.PERMISSION_CHECK_FAILED);
                                    setRetry(() => changeState(ProvisionState.INIT));
                                    changeMessage('Something went wrong trying to find the IOT.');
                                });
                        })
                        .catch((e) => {
                            ErrorHandler.handleError(e);
                            changeState(ProvisionState.PERMISSION_CHECK_FAILED);
                            setRetry(() => changeState(ProvisionState.INIT));
                            changeMessage('No token found.');
                        });
                } catch (err) {
                    Logger.warn(err);
                    changeState(ProvisionState.PERMISSION_CHECK_FAILED);
                    changeMessage(
                        'This device has not been provisioned correctly yet. Make sure it has been given the correct permissions and user interface has been linked.'
                    );
                    setRetry(() => changeState(ProvisionState.INIT));
                }
                break;
            case ProvisionState.DONE:
                props.provisionDone && props.provisionDone();
                break;
        }
    }, [state]);

    useEffect(() => {
        if (!provisionInfo) return;

        dispatch(setProvisioning(provisionInfo));
        authenticationManager.registerProvision(provisionInfo);
    }, [authenticationManager, dispatch, provisionInfo]);

    return { state, message };
};

export default useProvision;

function setRetry(callback: () => void) {
    const retry: NodeJS.Timeout = setTimeout(() => {
        DriverAuthenticationManager.getInstance('token').logout();
        callback();
        return clearTimeout(retry);
    }, 60000);
}
